import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ModalType } from '@components/modals';
import { useModal } from '@store/hooks/useModal';

import { defaultActionTargets } from '../actionTargets';
import { useActionTargetOverrides, useServerUiScope } from '../components/ServerUiDomainProvider';
import { useDismissComponentMutation } from '../store';
import { useInvalidateAndRefetch } from '../store/hooks';
import type { Action } from '../types';

/**
 * Returns a dispatch function that handles any server-ui Action by kind.
 *
 * For `modal` actions, resolves the target through a three-layer lookup:
 *   1. Consumer overrides (via ServerUiDomainProvider actionTargets prop)
 *   2. Default action targets (in server-ui/actionTargets.ts)
 *   3. Raw passthrough (action.target as modal key, action.payload as props)
 *
 * `source` analytics tagging: the backend is the authority. Any `source`
 * string on `action.payload` is threaded onto the modal props so downstream
 * tracking can attribute the open to a specific surface (e.g. the BE labels
 * a patient-page last_session "Read Full Note" action as
 * `patient_page_last_session`). When the backend doesn't supply one, we fall
 * back to the originating component's `type_key` so events still have a
 * coarse source rather than nothing.
 */
export const useActionDispatch = (typeKey: string) => {
  const navigate = useNavigate();
  const { open } = useModal();
  const { containerKey, entityId } = useServerUiScope();
  const [dismiss] = useDismissComponentMutation();
  const invalidateAndRefetch = useInvalidateAndRefetch();
  const overrides = useActionTargetOverrides();

  return useCallback(
    async (action: Action) => {
      switch (action.kind) {
        case 'navigate':
          if (action.target) navigate(action.target);
          break;

        case 'modal': {
          if (!action.target) break;
          const mapping = overrides[action.target] ?? defaultActionTargets[action.target];
          const payload = action.payload ?? {};
          const payloadSource = (payload as { source?: unknown }).source;
          const source = typeof payloadSource === 'string' ? payloadSource : typeKey;

          if (mapping) {
            const transformed = mapping.transformPayload
              ? mapping.transformPayload(payload)
              : payload;
            // `source` is placed last so the computed fallback (payload.source
            // when it's a string, otherwise typeKey) wins over anything the
            // spreads might bring in — notably `source: null` from the
            // backend, which otherwise clobbers the typeKey fallback.
            open(mapping.modalType as ModalType, {
              ...mapping.defaultProps,
              ...transformed,
              source,
            });
          } else {
            // Raw passthrough — no mapping exists
            open(action.target as ModalType, { ...payload, source });
          }
          break;
        }

        case 'dismiss':
          if (containerKey && entityId) {
            await dismiss({ containerKey, typeKey, entityId }).unwrap();
            await invalidateAndRefetch();
          }
          break;

        case 'chat_scope':
          break;

        case 'noop':
          break;
      }
    },
    [navigate, open, containerKey, entityId, typeKey, dismiss, invalidateAndRefetch, overrides],
  );
};
